import { roleAtLeast, UserRole } from '@atlas/types'
import { useAuth } from '@/hooks/useAuth'

/**
 * What the signed-in role may do, in the same three cuts the backend gates on
 * (`WORK_ROLES`, `ADMIN_ROLES`, `SUPER_ADMIN_ROLES` in `@atlas/types`).
 *
 * Reading is absent on purpose: every role reads the whole non-admin product,
 * so a read capability would always be true and would only invite someone to
 * branch on it.
 *
 * This is UX, never enforcement — the server is authoritative and a 403 still
 * has to render. The point is that a role is not offered an action it cannot
 * complete, which is a different job from stopping it.
 */
export interface Capabilities {
	/** Create, edit, transition, comment, upload, classify, publish. */
	readonly canWork: boolean
	/** The admin section: taxonomy and workflow configuration. */
	readonly canConfigure: boolean
	/** Users, roles, webhooks, external integrations. */
	readonly canManageTenant: boolean
}

export function capabilitiesFor(role: UserRole | string | undefined | null): Capabilities {
	const r = role ?? ''
	return {
		canWork: roleAtLeast(r, UserRole.USER),
		canConfigure: roleAtLeast(r, UserRole.ADMIN),
		canManageTenant: roleAtLeast(r, UserRole.SUPER_ADMIN),
	}
}

/**
 * Deletion is the one action scoped by ownership rather than by role alone: you
 * may remove your own work, an administrator may remove anyone's.
 *
 * `ownerId` is nullable because `created_by` is — a row written before it was
 * populated belongs to nobody and is administrator-only.
 */
export function canDeleteOwnedBy(
	role: UserRole | string | undefined | null,
	userId: string | undefined | null,
	ownerId: string | null | undefined,
): boolean {
	if (roleAtLeast(role ?? '', UserRole.ADMIN)) return true
	return !!ownerId && !!userId && ownerId === userId
}

/** The signed-in user's capabilities. */
export function useCan(): Capabilities {
	const { user } = useAuth()
	return capabilitiesFor(user?.role)
}
